import type { QueryClient } from '@tanstack/react-query';
import type { Account, MailApi } from '@/api';
import { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';

/**
 * Re-entering a connected mailbox's own password: the one flow behind
 * Settings → Accounts' form and the shell's re-auth modal (#192), so the two
 * cannot drift on what is sent, what is refreshed or what is said.
 */

/** The connected mailboxes' background-notification status (NotificationsSettings, CredentialBanner). */
export const BACKGROUND_ACCOUNTS_QUERY = ['push', 'background-accounts'] as const;

/*
 * Saving a connected mailbox's password withdraws the copy background
 * notifications keep for it (verifyAccount): it is the OLD password, and the
 * owner's rule is that turning them on again is a fresh consent.
 */
export const BACKGROUND_OFF_BEFORE_SAVE =
  'If background notifications are on for this mailbox, saving turns them off; turn them on again in Settings → Notifications.';
export const BACKGROUND_OFF_AFTER_SAVE =
  'Background notifications for it, if they were on, are now off — turn them on again in Settings → Notifications.';

/**
 * POST /accounts/{id}/verify with the password, once. Never retried: a wrong
 * password counts against the lockout every connection from this server
 * shares (plan 06 §6).
 *
 * The answer is the account, so the list is patched with it at once — the
 * banner row and the amber dot go in the same render the modal closes in —
 * and then re-read, along with everything this mailbox failed to load while
 * it was flagged.
 */
export async function reenterPassword(
  api: MailApi,
  queryClient: QueryClient,
  account: Pick<Account, 'id'>,
  password: string,
): Promise<Account> {
  const updated = await api.accounts.verify(account.id, password);
  queryClient.setQueryData<Account[]>(queryKeys.accounts(), (list) =>
    list?.map((one) => (one.id === updated.id ? updated : one)),
  );
  void queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).all() });
  // The server withdrew this mailbox's background-notification password with
  // it (accounts_connected.go, verifyAccount): the Notifications pane must ask
  // again rather than show it still on.
  void queryClient.invalidateQueries({ queryKey: BACKGROUND_ACCOUNTS_QUERY });
  return updated;
}

/** A server message shown verbatim, plus the lockout budget when it has one. */
export function accountActionMessage(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError) {
    // The per-mailbox re-verify cap (#30): the server names the mailbox, and
    // the refusal is about THAT mailbox, not this address or the sign-in. If a
    // message ever arrives without the address, it is added rather than left
    // for the reader to guess which mailbox is meant.
    const capped = cause.isRateLimited ? cause.details?.account_email : undefined;
    if (typeof capped === 'string' && capped !== '') {
      return cause.message.includes(capped) ? cause.message : `${capped}: ${cause.message}`;
    }
    const remaining = cause.details?.attempts_remaining;
    if (typeof remaining === 'number') {
      const stop = /[.!?]$/.test(cause.message) ? '' : '.';
      return `${cause.message}${stop} ${remaining} attempt${remaining === 1 ? '' : 's'} left.`;
    }
    return cause.message;
  }
  return cause instanceof Error ? cause.message : fallback;
}
